import Papa from "papaparse";

const REQUIRED_HEADER_KEYS = [
  "car_id",
  "make",
  "model",
  "grade",
  "body",
  "fuel",
  "description",
  "engine",
  "power",
  "transmission",
  "drivetrain",
  "speed",
  "maxspeed",
  "consumption",
  "co2",
  "fuelron",
  "fueltank",
  "erange",
  "warranty",
  "country",
  "length",
  "width",
  "height",
  "wheelbase",
  "seats",
  "kerbweight",
  "cargo",
  "ancap",
  "airbags",
  "spare",
  "alternative1",
  "alternative2",
  "alternative3",
];

const OPTIONAL_HEADER_KEYS = ["battery", "towing"];

const REQUIRED_ROW_KEYS =
  "car_id,make,model,grade,body,fuel,description,engine,power,transmission,drivetrain,speed,maxspeed,consumption,co2,fuelron,fueltank,erange,warranty,country,length,width,height,wheelbase,seats,kerbweight,cargo,ancap,airbags,spare,alternative1,alternative2,alternative3";

function checkRequiredHeaders(metaFields) {
  if (!metaFields || !Array.isArray(metaFields)) return false;
  const set = new Set(metaFields);
  return REQUIRED_HEADER_KEYS.every((key) => set.has(key));
}

export function checkNull(item, keys) {
  const keysToCheck = keys.split(",");
  if (!Array.isArray(keysToCheck)) {
    throw new Error("keysToCheck must be an array");
  }

  return keysToCheck.some((key) => {
    const v = item[key];
    return v === null || v === undefined || v === "";
  });
}

/** Empty row from Papa (trailing newlines) — do not count as invalid */
function isRowEffectivelyEmpty(item) {
  if (!item || typeof item !== "object") return true;
  const vals = Object.values(item);
  return vals.every((v) => v === null || v === undefined || v === "");
}

/** Optional string fields: null, undefined, and "" are all stored as "" */
function normalizeOptionalString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeVehicleRow(item) {
  const row = { ...item };

  for (const key of REQUIRED_HEADER_KEYS) {
    if (row[key] === null || row[key] === undefined || row[key] === "") {
      row[key] = key === "ancap" || key === "airbags" ? 0 : "";
    } else if (key !== "ancap" && key !== "airbags") {
      row[key] = String(row[key]).trim();
    }
  }

  row.battery = normalizeOptionalString(row.battery);
  row.towing = normalizeOptionalString(row.towing);

  if (typeof row.car_id === "string") {
    row.car_id = row.car_id.trim();
  }

  row.airbags = normalizeRatingNumber(row.airbags);
  row.ancap = normalizeRatingNumber(row.ancap);

  return row;
}

/** Coerce ancap / airbags to a number Mongo will accept */
function normalizeRatingNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const text = String(value ?? "")
    .trim()
    .toUpperCase();
  if (
    !text ||
    text === "NA" ||
    text === "N/A" ||
    text === "TBC" ||
    text === "NOT RATED" ||
    text === "-"
  ) {
    return -1;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : -1;
}

export function validateCSV(file) {
  return new Promise((resolve, reject) => {
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => String(header).replace(/^\uFEFF/, "").trim(),
        dynamicTyping: (field) => field === "ancap" || field === "airbags",
        complete: (results) => {
          const { data, meta } = results;

          console.log(meta.fields);

          if (!checkRequiredHeaders(meta.fields))
            return reject({
              message:
                "Please make sure these columns are present (names are case sensitive): car_id, make, model, grade, body, fuel, description, engine, power, transmission, drivetrain, speed, maxspeed, consumption, co2, fuelron, fueltank, erange, warranty, country, length, width, height, wheelbase, seats, kerbweight, cargo, ancap, airbags, spare, alternative1, alternative2, alternative3. Optional: battery, towing.",
              error: {},
            });

          let validDataCount = 0,
            invalidDataCount = 0;

          const validData = [];

          data.forEach((item) => {
            if (isRowEffectivelyEmpty(item)) {
              return;
            }

            if (checkNull(item, REQUIRED_ROW_KEYS)) {
              invalidDataCount += 1;
            } else {
              validDataCount += 1;
              validData.push(normalizeVehicleRow(item));
            }
          });

          resolve({
            validDataCount,
            invalidDataCount,
            validData,
          });
        },
      });
    } catch (error) {
      reject({
        message: "File could not be parsed",
        error,
      });
    }
  });
}
